import type { PostMedia, PostPlatform } from '../../types/publishing.js';
import { toIso, toPostPlatform } from './externalPosts.js';

// Publishing — per-post statistics (2026-07-20, brief Part B).
//
// Pure: normalization + the availability rules. `getAnalytics({ postId })` accepts
// BOTH Zernio and external post ids and returns the same shape for each, so ONE
// stats view serves external posts and our own published posts alike.
//
// ═══ THE TWO HONESTY RULES THIS MODULE EXISTS TO ENFORCE ═══
//
// 1. STATS ARE A SNAPSHOT, NEVER LIVE. The row carries `analytics.lastUpdated`
//    and the envelope carries `overview.lastSync`. Every figure is as-of that
//    moment. Same posture as the community inbox's reaction counts: show the
//    as-of time, never imply real-time.
//
// 2. GOOGLE BUSINESS HAS NO ANALYTICS AT ALL. The probe returned ZERO rows for
//    `platform=googlebusiness` (filtered both by platform and by account id), and
//    there is no GBP insights endpoint. So a GBP post can sit on the calendar and
//    can never show statistics. Rendering zeros for it would read as "this post
//    got no engagement" — commercially the opposite of the truth. Absence of a
//    measuring instrument is not a measurement of zero.

// ---------------------------------------------------------------------------
// availability
// ---------------------------------------------------------------------------

export type StatsAvailability =
  /** At least one destination is measurable. */
  | 'available'
  /** Every destination is Google Business — nothing can ever be measured. */
  | 'gbp-only';

/** Platforms Zernio can report on. GBP is deliberately absent — see rule 2. */
const MEASURABLE: PostPlatform[] = ['facebook', 'instagram'];

export function isMeasurablePlatform(platform: PostPlatform): boolean {
  return MEASURABLE.includes(platform);
}

/**
 * Whether a post can have stats at all, from its destinations alone — decided
 * BEFORE any network call, so a GBP-only post never fires a request that would
 * come back empty and get misread as "no engagement".
 *
 * A MIXED post (FB + GBP) is 'available': the FB half is genuinely measurable.
 * The GBP half is then marked unmeasurable per-row rather than shown as 0.
 */
export function statsAvailability(platforms: PostPlatform[]): StatsAvailability {
  return platforms.some(isMeasurablePlatform) ? 'available' : 'gbp-only';
}

// ---------------------------------------------------------------------------
// normalized shape
// ---------------------------------------------------------------------------

// Observed metric block (brief Part B). Every field optional — a platform that
// doesn't report a metric omits it, and an omitted metric is NOT zero.
export interface PostMetrics {
  impressions: number | null;
  reach: number | null;
  likes: number | null;
  comments: number | null;
  shares: number | null;
  saves: number | null;
  clicks: number | null;
  // Meta unified impressions/video-plays into `views` post-Nov-2025 — there is no
  // separate video-plays field, `views` IS it.
  views: number | null;
  follows: number | null;
  engagementRate: number | null;
  // IG Reels only — average watch time + total view time (observed analytics
  // block, probe B1). Absent/0 on non-Reel posts.
  igReelsAvgWatchTime: number | null;
  igReelsVideoViewTotalTime: number | null;
}

export interface PlatformStatsRow {
  platform: PostPlatform;
  /** false for Google Business — render "not available", never zeros. */
  measurable: boolean;
  metrics: PostMetrics | null;
  permalink: string | null;
  /** Zernio's own sync state for this destination, when it reports one. */
  syncStatus: string | null;
  error: string | null;
}

export interface PostStats {
  postId: string;
  /** null for external posts; set for ours (the originating kyck post id). */
  latePostId: string | null;
  isExternal: boolean;
  isAd: boolean;
  content: string;
  publishedAt: string | null;
  permalink: string | null;
  thumbnailUrl: string | null;
  mediaType: string | null;
  media: PostMedia[];
  /** Post-level roll-up across destinations. */
  metrics: PostMetrics | null;
  platforms: PlatformStatsRow[];
  /** As-of time for the figures. THE honesty field — never render stats without it. */
  lastUpdated: string | null;
  /** Envelope-level sync time, when present. */
  lastSync: string | null;
}

// ---------------------------------------------------------------------------
// normalization (tolerant — see the provenance note in externalPosts.ts)
// ---------------------------------------------------------------------------

function num(v: unknown): number | null {
  if (typeof v === 'number' && Number.isFinite(v)) return v;
  // A numeric string is plausible from a JSON API; anything else is not a number.
  if (typeof v === 'string' && v.trim() !== '' && Number.isFinite(Number(v))) return Number(v);
  return null;
}
function str(v: unknown): string | null {
  return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
}

/**
 * Read a metric block. Returns null when the block is absent OR carries no
 * recognised metric — distinct from "all metrics are zero", which is a real
 * measurement. The UI renders null as "—", not as 0.
 */
export function toMetrics(raw: unknown): PostMetrics | null {
  if (!raw || typeof raw !== 'object') return null;
  const m = raw as Record<string, unknown>;
  const metrics: PostMetrics = {
    impressions: num(m.impressions),
    reach: num(m.reach),
    likes: num(m.likes),
    comments: num(m.comments),
    shares: num(m.shares),
    saves: num(m.saves),
    clicks: num(m.clicks),
    views: num(m.views),
    follows: num(m.follows),
    engagementRate: num(m.engagementRate),
    igReelsAvgWatchTime: num(m.igReelsAvgWatchTime),
    igReelsVideoViewTotalTime: num(m.igReelsVideoViewTotalTime),
  };
  return Object.values(metrics).some((v) => v !== null) ? metrics : null;
}

function toPlatformRow(raw: unknown): PlatformStatsRow | null {
  if (!raw || typeof raw !== 'object') return null;
  const p = raw as Record<string, unknown>;
  const platform = toPostPlatform(p.platform);
  if (!platform) return null;
  const measurable = isMeasurablePlatform(platform);
  return {
    platform,
    measurable,
    // Never surface a metric block for an unmeasurable platform, even if one
    // somehow arrives — a stray zero here is exactly the misread rule 2 forbids.
    metrics: measurable ? toMetrics(p.analytics) : null,
    permalink: str(p.platformPostUrl),
    syncStatus: str(p.syncStatus),
    error: str(p.errorMessage),
  };
}

/**
 * Normalize a `getAnalytics({ postId })` response into the stats view's shape.
 *
 * Handles BOTH observed response variants: the single-post shape keys its
 * per-destination array `platformAnalytics`, the list shape keys it `platforms`.
 * Both are read, because which one comes back depends on how the query was made
 * and we should not be brittle about it.
 */
export function normalizePostStats(
  raw: unknown,
  fallback: { postId: string; platforms?: PostPlatform[] },
): PostStats | null {
  if (!raw || typeof raw !== 'object') return null;
  const r = raw as Record<string, unknown>;

  const perPlatformRaw = Array.isArray(r.platformAnalytics)
    ? r.platformAnalytics
    : Array.isArray(r.platforms)
      ? r.platforms
      : [];
  const platforms = perPlatformRaw
    .map(toPlatformRow)
    .filter((p): p is PlatformStatsRow => p !== null);

  // A response with no per-platform breakdown still deserves rows, so the view
  // can state per-destination availability (notably: "GBP — not available").
  const rows =
    platforms.length > 0
      ? platforms
      : (fallback.platforms ?? []).map((platform) => ({
          platform,
          measurable: isMeasurablePlatform(platform),
          metrics: null,
          permalink: null,
          syncStatus: null,
          error: null,
        }));

  const metrics = toMetrics(r.analytics);

  return {
    postId: str(r.postId) ?? str(r._id) ?? fallback.postId,
    latePostId: str(r.latePostId),
    isExternal: r.isExternal === true,
    isAd: r.isAd === true,
    content: str(r.content) ?? '',
    publishedAt: toIso(r.publishedAt) ?? toIso(r.scheduledFor),
    permalink: str(r.platformPostUrl) ?? rows.find((p) => p.permalink)?.permalink ?? null,
    thumbnailUrl: str(r.thumbnailUrl),
    mediaType: str(r.mediaType),
    media: (Array.isArray(r.mediaItems) ? r.mediaItems : [])
      .map((item): PostMedia | null => {
        if (!item || typeof item !== 'object') return null;
        const i = item as Record<string, unknown>;
        const url = str(i.url) ?? str(i.thumbnail);
        if (!url) return null;
        return {
          type: str(i.type)?.toLowerCase() === 'video' ? 'video' : 'image',
          url,
          source: 'external',
          thumbnailUrl: str(i.thumbnail) ?? null,
        };
      })
      .filter((m): m is PostMedia => m !== null),
    metrics,
    platforms: rows,
    lastUpdated: toIso((r.analytics as Record<string, unknown> | undefined)?.lastUpdated),
    lastSync: null,
  };
}

// ---------------------------------------------------------------------------
// display helpers
// ---------------------------------------------------------------------------

/** The metrics worth showing, in a stable order, skipping the ones not reported. */
export const METRIC_LABELS: Array<{ key: keyof PostMetrics; label: string }> = [
  { key: 'impressions', label: 'Impressions' },
  { key: 'reach', label: 'Reach' },
  { key: 'likes', label: 'Likes' },
  { key: 'comments', label: 'Comments' },
  { key: 'shares', label: 'Shares' },
  { key: 'saves', label: 'Saves' },
  { key: 'clicks', label: 'Clicks' },
  { key: 'views', label: 'Views' },
  { key: 'follows', label: 'Follows' },
];

/**
 * As-of wording for the figures. Deliberately explicit ("as of …") rather than a
 * bare timestamp — the point is that the reader understands these are a snapshot,
 * and an unadorned time reads as "now".
 */
export function formatStatsAsOf(lastUpdated: string | null, now: Date): string {
  if (!lastUpdated) return 'Last sync time unknown';
  const then = Date.parse(lastUpdated);
  if (Number.isNaN(then)) return 'Last sync time unknown';
  const mins = Math.max(0, Math.round((now.getTime() - then) / 60000));
  if (mins < 60) return `as of ${mins === 0 ? 'just now' : `${mins} min ago`}`;
  const hours = Math.round(mins / 60);
  if (hours < 24) return `as of ${hours}h ago`;
  return `as of ${Math.round(hours / 24)}d ago`;
}
